// La pagina profilo (tela, artboard 48): si apre dal nome in fondo al menu. Avatar, nome
// visualizzato, password e passkey di chi è entrato; vale per ogni ruolo, visitatore compreso.
import { useState } from 'react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Field, TextField } from '../../components/Field';
import { ApiError, api, ROLE_LABEL } from '../../lib/api';
import { POP_ROLES, popVariants } from '../../lib/pops';
import { UserAvatar } from '../../shell/Sidebar';
import { useStore } from '../../store/StoreProvider';
import { PasskeysCard } from './PasskeysCard';

const MAX_IMAGE = 2 * 1024 * 1024;

export function Profile() {
  const { state, dispatch, openModal, toast } = useStore();
  const [display, setDisplay] = useState(state.display);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const name = state.display || state.user;

  const fail = (title: string) => (e: unknown) =>
    toast({
      kind: 'danger',
      title,
      body: e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.',
    });

  const avatar = (body: { pop?: string; image?: string | null }) =>
    api
      .saveAvatar(body)
      .then((a) => {
        dispatch({ type: 'auth/profile', avatar: a.avatar });
        setPicking(false);
        toast({ kind: 'success', title: 'Avatar aggiornato' });
      })
      .catch(fail('Avatar non salvato'));

  const upload = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_IMAGE) {
      toast({ kind: 'danger', title: 'Immagine troppo grande', body: 'Al massimo 2 MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => void avatar({ image: String(reader.result) });
    reader.readAsDataURL(file);
  };

  const save = () => {
    setBusy(true);
    api
      .saveAccount(display)
      .then((a) => {
        dispatch({ type: 'auth/profile', display: a.display });
        toast({ kind: 'success', title: 'Profilo salvato' });
      })
      .catch(fail('Profilo non salvato'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="page profile-page">
      <header className="profile-head">
        <UserAvatar avatar={state.avatar} name={name} size={72} />
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <h1 className="t-h1" style={{ margin: 0 }}>
            {name}
          </h1>
          <span className="t-caption dim" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {state.user}
            <span className="role-badge" data-role={state.role}>
              {ROLE_LABEL[state.role]}
            </span>
          </span>
        </div>
      </header>

      <div className="profile-grid">
        <Card>
          <div className="settings-block">
            <div className="settings-block-text">
              <span className="t-overline faint">Profilo</span>
              <span className="t-strong">Avatar</span>
              <span className="t-caption dim">
                Un pop di PopTown o una tua immagine (PNG, JPEG o WebP, fino a 2 MB).
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label className="btn-file">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => upload(e.target.files?.[0])}
                />
                Carica immagine
              </label>
              <Button size="sm" onClick={() => setPicking((v) => !v)}>
                {picking ? 'Chiudi i pop' : 'Scegli un pop'}
              </Button>
              {state.avatar && (
                <Button size="sm" variant="ghost" onClick={() => void avatar({ image: null })}>
                  Togli
                </Button>
              )}
            </div>
          </div>
          {picking && (
            <div className="profile-pops" role="listbox" aria-label="Pop per l'avatar">
              {POP_ROLES.flatMap((r) =>
                popVariants(r.id).map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="option"
                    aria-selected={state.avatar === `/pops/pop-${v}.webp`}
                    aria-label={r.label}
                    title={r.label}
                    onClick={() => void avatar({ pop: `pop-${v}` })}
                  >
                    <img src={`/pops/pop-${v}.webp`} alt="" width={44} height={44} />
                  </button>
                )),
              )}
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <Field
              label="Nome visualizzato"
              hint={`Compare nel menu. Per entrare resta «${state.user}».`}
            >
              <TextField value={display} onChange={setDisplay} />
            </Field>
          </div>
          <div className="settings-foot">
            <Button
              variant="primary"
              icon="check"
              loading={busy}
              disabled={!display.trim() || display.trim() === state.display}
              onClick={save}
            >
              Salva profilo
            </Button>
          </div>
        </Card>

        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <Card>
            <div className="settings-block">
              <div className="settings-block-text">
                <span className="t-overline faint">Sicurezza</span>
                <span className="t-strong">Password</span>
                <span className="t-caption dim">
                  Almeno 8 caratteri. Cambiandola si chiudono le tue altre sessioni.
                </span>
              </div>
              <Button
                size="sm"
                icon="key-round"
                onClick={() => openModal({ kind: 'change-password', title: 'Cambia password' })}
              >
                Cambia password
              </Button>
            </div>
          </Card>
          <PasskeysCard />
        </div>
      </div>
    </div>
  );
}
